import React from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { PressEvent, Size } from '../../types';
import { buttonStyles } from '../button/button';
import { TooltipSeat } from '../tooltip/tooltip';

/** The field sizes that seat an action. A 24px field has no room for one:
 * the smallest action is its own height. */
export type ActionFieldSize = Exclude<Size, 'sm'>;

/** Set by the field that seats the action: its size, whether the whole
 * field is disabled, and whether the action comes and goes with the value
 * (a search's clear), so it enters with motion rather than popping in.
 * Shared by direct import, never through the barrel. */
export const FieldActionContext = React.createContext<{
  size: ActionFieldSize;
  isDisabled: boolean;
  isTransient?: boolean;
  /** A press leaves focus in the entry: a combobox commits on the entry's blur. */
  keepsEntryFocus?: boolean;
  /** Seated beside a row of chips, whose height the frame keeps. */
  besideChips?: boolean;
}>({ size: 'md', isDisabled: false });

/* One step down the Button ramp, so the action sits inside the frame with an
 * inset on every side: a 24px action in the 32px field, 32px in the 40px. */
const ACTION_SIZE: Record<ActionFieldSize, Size> = { md: 'sm', lg: 'md' };

/* Beside a row of chips the seat overhangs the chip's height by this much
 * on each side, so the frame keeps the field's height. */
const CHIPS_ROW_OVERHANG: Record<ActionFieldSize, string> = {
  md: 'ck:my-chips-seat',
  lg: 'ck:my-chips-seat-lg',
};

/* After another seat, a seat pulls in by its own inset (the square less its
 * 16px glyph, halved), so the two glyphs sit as far apart as a seat's glyph
 * and a bare one: one group at the field's end. */
const AFTER_SEAT_PULL: Record<ActionFieldSize, string> = {
  md: 'ck:-ms-1',
  lg: 'ck:-ms-2',
};

/**
 * The seat at a field's end as a picker's chevron wears it: the clear's
 * ghost square, so the end of every field reads alike and its smallest
 * target is the system's 24px. Wear it with `data-tone="neutral"`. In an
 * inert field it is a glyph only: no wash, presses fall through to the
 * frame, and the field's own wash is the only fade.
 */
export function fieldSeatClass(
  size: ActionFieldSize,
  {
    isInert = false,
    besideChips = false,
    afterSeat = false,
  }: { isInert?: boolean; besideChips?: boolean; afterSeat?: boolean } = {},
) {
  return buttonStyles({
    variant: 'ghost',
    tone: 'neutral',
    size: ACTION_SIZE[size],
    shape: 'square',
    pending: false,
    ariaDisabled: false,
  }).root({
    class: [
      isInert && 'ck:pointer-events-none ck:disabled:opacity-100',
      besideChips && CHIPS_ROW_OVERHANG[size],
      afterSeat && AFTER_SEAT_PULL[size],
    ],
  });
}

export interface FieldActionProps {
  /** The glyph: a heroicons 24/outline component. */
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Names the action ("Show password", "Copy link"); shown as its tooltip. */
  label: string;
  /** What the action does. */
  onPress?: (e: PressEvent) => void;
  /**
   * Whether the action alone is unavailable. It stays focusable with its
   * name one hover away, as a disabled IconButton does. A disabled field
   * disables its action outright instead: the field's description says why.
   */
  isDisabled?: boolean;
}

/**
 * The one action a single-line field carries at its end (a reveal, a copy),
 * passed to the field's `action` prop. The field sizes and seats it: a
 * neutral ghost square one step down the ramp, in the frame's flow so typed
 * text never runs under it.
 *
 * It renders Button's own recipe rather than an IconButton because its glyph
 * matches the field's text (16px), not the button step's: the 24px icon
 * button carries a 12px glyph, which beside a 16px start icon reads as a
 * different family.
 */
export const FieldAction = React.forwardRef<HTMLButtonElement, FieldActionProps>(
  function FieldAction({ icon: Glyph, label, onPress, isDisabled = false }, ref) {
    const field = React.useContext(FieldActionContext);
    /* The aria-disabled form, as IconButton takes it in a tooltip seat:
     * React Aria's pending machinery keeps the button focusable and its
     * hover events alive while stripping presses and exposing aria-disabled,
     * so the name stays reachable. */
    const seated = isDisabled && !field.isDisabled;
    const { root } = buttonStyles({
      variant: 'ghost',
      tone: 'neutral',
      size: ACTION_SIZE[field.size],
      shape: 'square',
      pending: false,
      ariaDisabled: seated,
    });
    return (
      <TooltipSeat content={label} placement="top" delay={0}>
        <AriaButton
          ref={ref}
          aria-label={label}
          onPress={seated ? undefined : onPress}
          isDisabled={field.isDisabled}
          isPending={seated}
          preventFocusOnPress={field.keepsEntryFocus}
          className={root({
            class: [
              field.isTransient && 'ck:animate-enter',
              field.besideChips && CHIPS_ROW_OVERHANG[field.size],
            ],
          })}
          data-component="field-action"
          data-tone="neutral"
        >
          <Glyph aria-hidden="true" focusable="false" className="ck:size-4 ck:shrink-0" />
        </AriaButton>
      </TooltipSeat>
    );
  },
);
